export enum CommissionPlanStatus {
  ACTIVE = "Active",
  INACTIVE = "Inactive",
  DRAFT = "Draft",
  REPLACED = "Replaced"
}

export enum PreferredLanguage {
  English = "en",
  French = "fr"
}

export enum PreferredCultureId {
  English = 48,
  French = 88
}

export enum RateType {
  PERCENTAGE = "Percentage",
  FLAT_FEE = "FlatFee"
}

export enum TierBasis {
  TRANSACTION_GROSS_MARGIN = "TransactionGrossMargin",
  TRANSACTION_GROSS_MARGIN_PERCENTAGE = "TransactionGrossMarginPercentage",
  TRANSACTION_REVENUE = "TransactionRevenue",
  MONTHLY_GROSS_MARGIN = "MonthlyGrossMargin",
  MONTHLY_REVENUE = "MonthlyRevenue",
  ANNUAL_GROSS_MARGIN = "AnnualGrossMargin",
  ANNUAL_REVENUE = "AnnualRevenue",
  WO_HOURLY_GROSS_MARGIN_PERCENTAGE = "WOHourlyGrossMarginPercentage",
  WO_HOURLY_GROSS_MARGIN = "WOHourlyGrossMargin",
  WO_HOURLY_GROSS_MARGIN_PERCENTAGE_OR_AMOUNT = "WOHourlyGrossMarginPercentageOrAmount",
  // one basis carrying a Measure + a configurable ResetSchedule (see interface.ts).
  // Window/measure are sub-fields rather than baked into the basis, so future cadences are configurable.
  CONFIGURABLE_ACCUMULATOR = "ConfigurableAccumulator"
}

// Measure for the Configurable Accumulator basis: WHAT the tiers accumulate on.
export enum Measure {
  GROSS_MARGIN = "GrossMargin",
  REVENUE = "Revenue",
  GROSS_MARGIN_PERCENTAGE = "GrossMarginPercentage"
}

// ResetSchedule.Type — how the accumulation window is bounded (see ResetSchedule in interface.ts).
//   NO_ACCUMULATION : no window — each transaction stands alone.
//   FULL_PERIOD     : the window IS one full interval (IntervalUnit × IntervalCount) anchored to a
//                     date. Covers weekly, biweekly, monthly, quarterly, annual. End-of-window is
//                     derived from the unit (the unit is the length).
// FUTURE — subdividing a period (e.g. semi-monthly)
export enum ScheduleType {
  NO_ACCUMULATION = "NoAccumulation",
  FULL_PERIOD = "FullPeriod"
}

export enum IntervalUnit {
  DAY = "Day",
  WEEK = "Week",
  MONTH = "Month",
  YEAR = "Year"
}

// cadence presets offered so far. The full engine supports more (weekly/biweekly/semi-monthly), but the
// UI is restricted to these for now. Each preset maps to a concrete ResetSchedule via cadenceToSchedule().
export enum Cadence {
  TRANSACTION = "Transaction",
  MONTHLY = "Monthly",
  QUARTERLY = "Quarterly",
  ANNUAL = "Annual"
}

export enum Trigger {
  REAL_TIME = "RealTime",
  ON_CLIENT_PAYMENT = "OnClientPayment"
}

// Calculation date basis for monthly/annual tier bases
export enum CalculationDateBasis {
  PROCESSING_DATE = "ProcessingDate",
  END_DATE = "PeriodEndDate"
}

// Display label mappings
export const RateTypeLabels: Record<string, string> = {
  [RateType.PERCENTAGE]: "Percentage",
  [RateType.FLAT_FEE]: "Flat fee"
}

export const TierBasisLabels: Record<string, string> = {
  [TierBasis.TRANSACTION_GROSS_MARGIN]: "Transaction gross margin",
  [TierBasis.TRANSACTION_GROSS_MARGIN_PERCENTAGE]: "Transaction gross margin %",
  [TierBasis.TRANSACTION_REVENUE]: "Transaction revenue",
  [TierBasis.MONTHLY_GROSS_MARGIN]: "Monthly gross margin",
  [TierBasis.MONTHLY_REVENUE]: "Monthly revenue",
  [TierBasis.ANNUAL_GROSS_MARGIN]: "Annual gross margin",
  [TierBasis.ANNUAL_REVENUE]: "Annual revenue",
  [TierBasis.WO_HOURLY_GROSS_MARGIN_PERCENTAGE]: "WO Hourly Gross Margin %",
  [TierBasis.WO_HOURLY_GROSS_MARGIN]: "WO Hourly Gross Margin",
  [TierBasis.WO_HOURLY_GROSS_MARGIN_PERCENTAGE_OR_AMOUNT]: "WO Hourly Gross Margin % or Amount",
  // User-facing label intentionally differs from the code value: "accumulation period" reads as the
  // window the tiers add up over, vs. internal "accumulator". Label can change with no data migration.
  [TierBasis.CONFIGURABLE_ACCUMULATOR]: "Configurable accumulation period"
}

export const MeasureLabels: Record<string, string> = {
  [Measure.GROSS_MARGIN]: "Gross margin",
  [Measure.REVENUE]: "Revenue",
  [Measure.GROSS_MARGIN_PERCENTAGE]: "Gross margin %"
}

export const CadenceLabels: Record<string, string> = {
  [Cadence.TRANSACTION]: "Per transaction",
  [Cadence.MONTHLY]: "Monthly",
  [Cadence.QUARTERLY]: "Quarterly",
  [Cadence.ANNUAL]: "Annually"
}

// Whether a cadence needs a "period starts in" anchor month (Quarterly/Annual only).
// Transaction has no period; Monthly always resets on the 1st, so neither needs an anchor month.
export function cadenceUsesAnchorMonth(cadence: Cadence): boolean {
  return cadence === Cadence.QUARTERLY || cadence === Cadence.ANNUAL
}

export const AccrualTriggerLabels: Record<string, string> = {
  [Trigger.REAL_TIME]: "Real time",
  [Trigger.ON_CLIENT_PAYMENT]: "On client payment"
}

export const PayoutTriggerLabels: Record<string, string> = {
  [Trigger.REAL_TIME]: "Real time",
  [Trigger.ON_CLIENT_PAYMENT]: "On client payment"
}

export const CalculationDateBasisLabels: Record<string, string> = {
  [CalculationDateBasis.PROCESSING_DATE]: "Processing date",
  [CalculationDateBasis.END_DATE]: "Period End date"
}

// Month labels for ResetMonth field (1-12)
export const MonthLabels: Record<number, string> = {
  1: "January",
  2: "February",
  3: "March",
  4: "April",
  5: "May",
  6: "June",
  7: "July",
  8: "August",
  9: "September",
  10: "October",
  11: "November",
  12: "December"
}
